<!-- 全局 AI 助手悬浮入口：在系统各页面左下角提供统一的对话入口。 -->
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import { useRoute, useRouter } from "vue-router";
import robotIcon from "@/assets/images/ai-assistant-robot.gif";
import { useUserStore } from "@/store/modules/user";

defineOptions({ name: "AiAssistantFloating" });

// 使用现有会话页面，保持本机历史记录与原入口一致。
const router = useRouter();
// 位置按账号独立保存，未登录时不读写共享的匿名位置。
const userStore = useUserStore();
// 登录信息恢复或切换账号后自动切换位置存储键。
const positionKey = computed(() => {
  // 账号 ID 为 0 时仍是有效标识，不能用真假判断。
  const userId = userStore.user.userId;
  return userId == null || String(userId) === ""
    ? null
    : `office-ai-assistant-position:${userId}`;
});
// 元素尺寸用于拖动和窗口缩放时限制图标位置。
const floatingButton = ref<HTMLButtonElement | null>(null);
// 无保存记录时沿用 CSS 的左下角默认位置，有记录时恢复账号的坐标。
const position = ref<{ x: number; y: number } | null>(null);
// 当前拖动手势只跟踪一个指针，避免多指触屏干扰。
let gesture: {
  // 发起拖动的指针编号。
  pointerId: number;
  // 按下时的指针横坐标。
  startX: number;
  // 按下时的指针纵坐标。
  startY: number;
  // 按下时按钮的左边界。
  left: number;
  // 按下时按钮的上边界。
  top: number;
} | null = null;
// 超过移动阈值后阻止拖动产生的点击，普通点击和键盘激活仍可跳转。
let suppressClick = false;
// 拖动时展示抓取光标。
const isDragging = ref(false);
// 显式坐标覆盖默认 bottom 定位，保持指针与图标的相对位置。
const floatingStyle = computed(() =>
  position.value
    ? {
        left: `${position.value.x}px`,
        top: `${position.value.y}px`,
        bottom: "auto",
      }
    : undefined
);

/**
 * 把图标限制在当前视口内，窗口缩小时也不丢失入口。
 * @param x 期望的左边界
 * @param y 期望的上边界
 */
function updatePosition(x: number, y: number): void {
  // 保留四周 8px 的空隙；极小视口下优先保证左上角仍可访问。
  const bounds = floatingButton.value?.getBoundingClientRect();
  if (!bounds) return;
  position.value = {
    x: Math.max(8, Math.min(x, window.innerWidth - bounds.width - 8)),
    y: Math.max(8, Math.min(y, window.innerHeight - bounds.height - 8)),
  };
}

/**
 * 捕获指针，让鼠标或触屏移出按钮后仍能继续拖动。
 * @param event 指针按下事件
 */
function startDrag(event: PointerEvent): void {
  if (event.button !== 0 || gesture) return;
  // 每次新手势清除上一次拖动的点击抑制状态。
  const bounds = floatingButton.value?.getBoundingClientRect();
  if (!bounds) return;
  suppressClick = false;
  gesture = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    left: bounds.left,
    top: bounds.top,
  };
  floatingButton.value?.setPointerCapture(event.pointerId);
}

/**
 * 超过 5px 才视为拖动，避免正常点击时手部轻微抖动。
 * @param event 指针移动事件
 */
function moveDrag(event: PointerEvent): void {
  if (!gesture || gesture.pointerId !== event.pointerId) return;
  // 位移基于按下位置累计，保持图标跟随指针而不发生跳动。
  const dx = event.clientX - gesture.startX;
  const dy = event.clientY - gesture.startY;
  if (!isDragging.value && Math.hypot(dx, dy) < 5) return;
  isDragging.value = true;
  suppressClick = true;
  updatePosition(gesture.left + dx, gesture.top + dy);
}

/**
 * 结束或取消拖动，释放当前手势状态。
 * @param event 指针结束事件
 */
function endDrag(event: PointerEvent): void {
  if (gesture?.pointerId !== event.pointerId) return;
  if (isDragging.value) savePosition();
  gesture = null;
  isDragging.value = false;
}

// VueUse 在组件卸载时自动移除监听，避免全局残留事件。
useEventListener(window, "resize", () => {
  if (position.value) updatePosition(position.value.x, position.value.y);
});

/** 保存拖动后的账号位置，存储不可用时仍允许正常拖动。 */
function savePosition(): void {
  if (!positionKey.value || !position.value) return;
  try {
    localStorage.setItem(positionKey.value, JSON.stringify(position.value));
  } catch {
    // 浏览器禁用本地存储时保留当前页面位置，不影响助手入口。
  }
}

// 同步重置手势，避免切换账号时把上一个用户的拖动结果写入新账号。
watch(
  positionKey,
  (key) => {
    gesture = null;
    isDragging.value = false;
    suppressClick = false;
    position.value = null;
    if (!key) return;
    try {
      // 本地数据可能被清理或修改，只有有限数值坐标才允许恢复。
      const saved: unknown = JSON.parse(localStorage.getItem(key) ?? "null");
      if (
        saved &&
        typeof saved === "object" &&
        "x" in saved &&
        "y" in saved &&
        typeof saved.x === "number" &&
        typeof saved.y === "number" &&
        Number.isFinite(saved.x) &&
        Number.isFinite(saved.y)
      ) {
        position.value = { x: saved.x, y: saved.y };
        updatePosition(saved.x, saved.y);
      }
    } catch {
      // 损坏记录回退到默认左下角，不阻断页面加载。
    }
  },
  { immediate: true }
);

// 图标重新挂载后按实际尺寸限制恢复位置，覆盖首次打开及从助手页面返回。
watch(
  floatingButton,
  (button) => {
    if (button && position.value)
      updatePosition(position.value.x, position.value.y);
  },
  { flush: "post" }
);
// 跟随当前路由，仅在 Office 业务区域提供悬浮入口。
const route = useRoute();
// Office 之外及助手自身页面隐藏入口，同时兼容地址末尾的斜杠。
const isVisible = computed(() => {
  // 按路径与路由名判断，避免助手页面重复显示悬浮入口。
  const path = route.path.replace(/\/$/, "");
  return (
    (path === "/office" || path.startsWith("/office/")) &&
    path !== "/office/ai-assistant" &&
    route.name !== "OfficeAiAssistant"
  );
});

/**
 * 打开 AI 助手，拖动结束产生的点击不执行跳转。
 * @param event 点击事件，键盘激活不受拖动抑制影响
 */
function openAssistant(event: MouseEvent): void {
  if (suppressClick && event.detail !== 0) {
    suppressClick = false;
    return;
  }
  void router.push({ name: "OfficeAiAssistant" });
}
</script>

<template>
  <Teleport to="body">
    <button
      v-if="isVisible"
      ref="floatingButton"
      type="button"
      class="ai-assistant-floating"
      :class="{ 'is-dragging': isDragging }"
      :style="floatingStyle"
      title="打开 AI 助手"
      aria-label="打开 AI 助手"
      @click="openAssistant"
      @pointerdown="startDrag"
      @pointermove="moveDrag"
      @pointerup="endDrag"
      @pointercancel="endDrag"
      @lostpointercapture="endDrag"
    >
      <img :src="robotIcon" alt="" draggable="false" />
    </button>
  </Teleport>
</template>

<style scoped lang="scss">
.ai-assistant-floating {
  position: fixed;
  // 放在 body 下避免全屏业务容器的 overflow 或 transform 裁切悬浮入口。
  bottom: calc(var(--spacing-page, 24px) + env(safe-area-inset-bottom, 0px));
  left: calc(var(--spacing-page, 24px) + env(safe-area-inset-left, 0px));
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 96px;
  padding: 0;
  color: var(--color-text);
  touch-action: none;
  cursor: pointer;
  user-select: none;
  background: transparent;
  border: 0;
  border-radius: 16px;

  img {
    display: block;
    width: 96px;
    height: 96px;
    object-fit: contain;
  }

  &:hover {
    cursor: grab;
  }

  &.is-dragging {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 4px;
  }
}

@media (width <= 768px) {
  .ai-assistant-floating {
    width: 76px;

    img {
      width: 76px;
      height: 76px;
    }
  }
}
</style>
